<script lang="ts" setup>
import { HeaderWrapper } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/project'
import { computed, inject } from 'vue'
import { useProjectUserStore } from '@/stores/projectUser'
import { useRoute, useRouter } from 'vue-router'
import { useCalculationStore } from '@/stores/calculation'
import { useDrawerStore } from '@/stores/drawer'
import { useHardwareProfileStore } from '@/stores/hardwareProfile'
import {
  CALCULATION_ROUTE_NAMES,
  HEADER_DRAWER_ROUTE_NAMES,
  PROJECT_SCOPED_ROUTE_NAMES
} from '@/router/routeNames'
import { resetAuthenticatedStores } from '@/utils/resetAuthenticatedStores'

type BreadcrumbItem = {
  title: string
  to?: Record<string, unknown> | null
}

const authStore = inject('useAuthStore') as any
const router = useRouter()
const route = useRoute()
const drawerStore = useDrawerStore()

const user = computed(() => authStore.user)
const {
  writeLoading: projectWriteLoading,
  deleteLoading: projectDeleteLoading,
  currentItem: currentProjectItem
} = storeToRefs(useProjectStore())
const { writeLoading: projectUserWriteLoading, deleteLoading: projectUserDeleteLoading } =
  storeToRefs(useProjectUserStore())
const {
  writeLoading: calculationWriteLoading,
  deleteLoading: calculationDeleteLoading,
  currentItem: currentCalculationItem
} = storeToRefs(useCalculationStore())
const { writeLoading: hardwareProfileWriteLoading, deleteLoading: hardwareProfileDeleteLoading } =
  storeToRefs(useHardwareProfileStore())

const loading = computed(() => {
  return (
    projectWriteLoading.value ||
    projectDeleteLoading.value ||
    projectUserWriteLoading.value ||
    projectUserDeleteLoading.value ||
    calculationWriteLoading.value ||
    calculationDeleteLoading.value ||
    hardwareProfileWriteLoading.value ||
    hardwareProfileDeleteLoading.value
  )
})

const currentRouteName = computed(() => route.name)

const isToggleButtonShown = computed(() => {
  const routeName = currentRouteName.value
  return (
    typeof routeName === 'string' &&
    HEADER_DRAWER_ROUTE_NAMES.includes(routeName as (typeof HEADER_DRAWER_ROUTE_NAMES)[number])
  )
})

const projectId = computed(() => {
  return route.params.id || currentProjectItem.value?.id || currentCalculationItem.value?.project
})

const projectBreadcrumbRoute = computed(() => {
  const projectId =
    route.params.id || currentProjectItem.value?.id || currentCalculationItem.value?.project
  if (!projectId) return null
  return {
    name: 'calculation-index',
    params: { id: projectId }
  }
})

const projectBreadcrumbTitle = computed(() => currentProjectItem.value?.name || 'Project')

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const routeName = currentRouteName.value
  if (typeof routeName !== 'string') return []

  if (routeName === 'project-index') {
    return [{ title: 'Projects' }]
  }

  if (routeName === 'global-hardware-profiles') {
    return [{ title: 'Hardware Profiles' }]
  }

  if (
    !PROJECT_SCOPED_ROUTE_NAMES.includes(
      routeName as (typeof PROJECT_SCOPED_ROUTE_NAMES)[number]
    ) ||
    !projectId.value
  )
    return []

  const crumbs: BreadcrumbItem[] = [
    { title: 'Projects', to: { name: 'project-index' } },
    {
      title: projectBreadcrumbTitle.value,
      to: routeName === 'calculation-index' ? undefined : projectBreadcrumbRoute.value
    }
  ]

  if (routeName === 'user-management') {
    crumbs.push({ title: 'User Management' })
  } else if (routeName === 'project-hardware-profiles') {
    crumbs.push({ title: 'Hardware Profiles' })
  } else if (
    CALCULATION_ROUTE_NAMES.includes(routeName as (typeof CALCULATION_ROUTE_NAMES)[number])
  ) {
    crumbs.push({
      title: currentCalculationItem.value?.name || `Calculation ${route.params.calculationId}`
    })
  }

  return crumbs
})

async function logout() {
  await authStore.logout()
  resetAuthenticatedStores()
  await router.push({ name: 'auth-login' })
}

const goToProjectIndex = () => {
  router.push({ name: 'project-index' })
}
</script>

<template>
  <HeaderWrapper
    v-if="user"
    title="CloudCompute"
    image-src="/logo-rhdhv-white.png"
    :user="user"
    :loading="loading"
    @logo-click="goToProjectIndex"
    @logout="logout()"
  >
    <template v-if="isToggleButtonShown" v-slot:toolbar-prepend>
      <v-app-bar-nav-icon
        class="pl-4 mx-1 no-overlay"
        height="36"
        width="36"
        color="white"
        :ripple="false"
        :icon="drawerStore.drawer.shown ? 'mdi-close' : 'mdi-menu'"
        @click="drawerStore.toggleDrawer"
      />
    </template>

    <template v-slot:breadcrumbs-items>
      <template v-for="(breadcrumb, index) in breadcrumbs" :key="`${breadcrumb.title}-${index}`">
        <v-breadcrumbs-divider v-if="index > 0" />
        <v-breadcrumbs-item>
          <router-link
            v-if="breadcrumb.to && index < breadcrumbs.length - 1"
            class="text-white toolbar-text text-decoration-none"
            :to="breadcrumb.to"
          >
            {{ breadcrumb.title }}
          </router-link>
          <span v-else class="text-white toolbar-text">
            {{ breadcrumb.title }}
          </span>
        </v-breadcrumbs-item>
      </template>
    </template>
  </HeaderWrapper>
</template>

<style scoped>
.no-overlay :deep(.v-btn__overlay) {
  display: none;
}
</style>
